<template>
    <div class="order-summary__summary">
        <p v-if="!isThisWeek && !hasPreorder" class="order-summary__paragraph m-0 pb-8">
            {{$t('SHOP_ORDER_ON_ITS_WAY_CUTOFF')}}
             <span v-if="!isThisWeek" class="order-summary__detail__delivery" v-text="`${deliveryDate} `"></span>
            {{$t('SHOP_ORDER_ON_ITS_WAY_CUTOFF_PRE_ORDER')}}
        </p>
        <thumbnail-product v-for="(product, key) in order.products" :item="product" :isThisWeek="isThisWeek" :activeSubscription="activeSubscription" :allowChange="!order.pastCutOff" :currency="currency" :key="key"/>
        <div class="order-summary__summary__costs">
            <p class="order-summary__detail" v-text="$t('SHOP_SUBTOTAL')"></p>
            <p class="order-summary__detail" v-text="`${currency}${order.subtotal}`"></p>
        </div>

        <!-- discount section -->
        <div v-if="discountAmount && isThisWeek && hasShopCoupon && isDEU" class="order-summary__summary__costs">
            <p class="order-summary__detail" v-text="$t('DISCOUNT')"></p>
            <p class="order-summary__detail" v-text="`${currency}${discountAmount}`"></p>
        </div>
        <div class="order-summary__summary__costs">
            <p class="order-summary__detail" v-text="$t('SHOP_DELIVERY')"></p>
            <p class="order-summary__detail" v-text="$t('SHOP_FREE')"></p>
        </div>
        <div class="order-summary__summary__costs">
            <p class="order-summary__detail__total" v-text="$t('SHOP_TOTAL')"></p>
            <p class="order-summary__detail__total" v-text="`${currency}${order.total}`" v-show="!isDEU"></p>
            <div class="orderSummary--amount" v-show="isDEU">
                <p class="orderSummary--amount--text" :class="{ 'strikeThrough': hasShopCoupon && isThisWeek }">{{`€${order.total}`}}</p>
                <p class="orderSummary--amount--text" v-if="hasShopCoupon && isThisWeek">{{`€${total}`}}</p>
            </div>
        </div>
        <p v-if="!hasMultipleCoupons && couponName && isThisWeek && isDEU" class="order-summary__paragraph mb-0" v-text="`${$t('SINGLE_SHOP_COUPON_APPLIED', {couponName: couponName})}`"></p>
        <p v-if="hasMultipleCoupons && isThisWeek && isDEU" class="order-summary__paragraph mb-0" v-text="`${$t('MULTIPLE_SHOP_COUPON_APPLIED')}`"></p>
        <p v-if="isThisWeek" class="order-summary__paragraph mb-0" v-text="`${$t('SHOP_ORDER_SUMMARY_DELIVERY')}`"></p>
        <p v-if="isThisWeek" class="order-summary__detail__delivery mt-0" v-text="`${deliveryDate}.`"></p>
    </div>
</template>

<script>
import { formatDisplayDate } from '@/helpers/DateHelpers';
import ThumbnailProduct from '@/components/partials/account/shop/order-summary/ThumbnailProduct';

export default {
    name: 'DeliverySummary',
    components: { ThumbnailProduct },
    props: {
        order: {
            required: true,
            type: Object,
        },
        currency: {
            required: true,
            type: String,
        },
        isThisWeek: {
            required: true,
            type: Boolean,
        },
        activeSubscription: {
            required: true,
            type: Object,
        },
    },
    computed: {
        deliveryDate() {
            return formatDisplayDate(this.order.delivery_date);
        },
        hasPreorder() {
            const preorder = this.order.products.find((item) => item.available_from);
            return preorder;
        },
        isDEU() {
            return process.env.VUE_APP_COUNTRY_ISO === 'DEU';
        },
        hasShopCoupon() {
            return this.order.shop_coupons && this.order.shop_coupons.length !== 0 && this.order.products;
        },
        hasMultipleCoupons() {
            return this.hasShopCoupon && this.order.shop_coupons?.length > 1;
        },
        couponApplied() {
            const amountCoupons = this.order.shop_coupons?.filter(item => item.discount_type.toLowerCase() === 'amount') || [];
            const percentageCoupons = this.order.shop_coupons?.filter(item => item.discount_type.toLowerCase() === 'percentage') || [];

            if (amountCoupons.length > 0) return amountCoupons;
            else if (percentageCoupons.length > 0) return percentageCoupons;
        },
        couponName() {
            if (this.hasShopCoupon && !this.hasMultipleCoupons) {
                return this.order.shop_coupons && this.order.shop_coupons[0].coupon_name;
            }
        },
        discountAmount() {
            if (!this.couponApplied) return;
            const discountType = (this.couponApplied.length !== 0) && this.couponApplied[0].discount_type;
            let discountValue = 0;
            if (discountType === 'amount' && this.couponApplied.length > 1) {
                let netDiscount = 0;
                this.couponApplied.forEach(item => {
                    netDiscount = netDiscount + item.discount;
                });
                discountValue = netDiscount;
            } else {
                discountValue = this.couponApplied.length !== 0 && this.couponApplied[0].discount;
            }

            if (discountType === 'percentage') {
                const saving = this.order?.total * (discountValue / 100);
                return saving.toFixed(2);
            } else if (discountType === 'amount') {
                const saving = discountValue * 0.01;
                return saving.toFixed(2);
            }
            return 0;
        },
        total() {
            const total = this.order.total - this.discountAmount;
            const finalTotal = total >= 0 ? total : 0;
            return (finalTotal).toFixed(2);
        },
    },

};
</script>

<style lang="scss" scoped>
.orderSummary{
    max-width: 400px;
    margin: 2rem auto;
    padding: 0 1rem;
    &--amount {
        display: flex;
        align-items: center;
        &--text {
            margin: 0 0.25rem;
            font-size: 20px;
            text-align: right;
            font-weight: 400;
            color: theme('colors.shop.black');
        }
        &--text.strikeThrough {
            margin: 0 0.5rem;
            text-decoration: line-through;
            color: theme('colors.shop.darkgrey');
            font-size: 17px;
            font-weight: lighter;
            line-height: 22px;
        }
    }
}
</style>
